import { memo, useMemo } from "react";
import { useWatch } from "react-hook-form";
import type { FieldValues } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { Form } from "@circle-react-uikit/Form";

export const otherOption = "other";

interface FormValues {
  reason: string;
  reason_description?: string;
}

interface ReasonFormFieldsProps {
  localeRoot: string;
  reasonKeys: string[];
}

const isFormValues = (values: FieldValues): values is FormValues =>
  typeof values.reason === "string";

const validateReasonDescription = (
  value: string,
  formValues: FieldValues,
  localeRoot: string,
) => {
  if (
    isFormValues(formValues) &&
    formValues.reason === otherOption &&
    (!value || value.trim() === "")
  ) {
    return t(`${localeRoot}.reason_description_required`);
  }
  return true;
};

export const ReasonFormFields = memo<ReasonFormFieldsProps>(
  ({ reasonKeys, localeRoot }) => {
    const reason = useWatch({ name: "reason" });

    const reasonOptions = useMemo(
      () =>
        reasonKeys.map(key => ({
          value: key,
          label: t(`${localeRoot}.reasons.${key}`),
        })),
      [reasonKeys, localeRoot],
    );

    const shouldShowReasonDescription = reason === otherOption;

    return (
      <>
        <Form.Item
          name="reason"
          label={t(`${localeRoot}.reason`)}
          rules={{
            required: t(`${localeRoot}.reason_required`),
          }}
          hideBorder
          hideDescription
          hideLabel
        >
          <Form.SelectNative
            options={reasonOptions}
            name="reason"
            prompt={t(`${localeRoot}.reason_prompt`)}
            defaultValue=""
          />
        </Form.Item>

        {shouldShowReasonDescription && (
          <Form.Item
            name="reason_description"
            label={t(`${localeRoot}.reason_description`)}
            rules={{
              validate: {
                requiredWhenOther: (value: string, formValues: FieldValues) =>
                  validateReasonDescription(value, formValues, localeRoot),
              },
            }}
            hideLabel
            hideDescription
            hideBorder
          >
            <Form.TextArea name="reason_description" />
          </Form.Item>
        )}
      </>
    );
  },
);

ReasonFormFields.displayName = "ReasonFormFields";
